<template>
  <vs-wrapper space="small">
    <vs-wrapper v-for="(group, index) in field.optionGroups" :key="index">
      <vs-card accent="var(--color-blue--lighter)">
        <v-row align="center">
          <v-col>
            <vs-text-input
              label="Group name"
              :value="group.title"
              @input="(newText) => updateGroupName(newText, index)"
            />
          </v-col>
        </v-row>
        <template v-if="!bulkEditActive(index)">
          <v-row
            v-for="(item, itemIndex) in group.items"
            :key="itemIndex"
            class="ml-6"
            align="center"
          >
            <v-col class="mr-n4">
              <vs-text-input
                label="Option label"
                :value="item"
                @input="(newText) => updateOptionText(newText, index, itemIndex)"
              />
            </v-col>
            <v-col cols="auto">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-icon
                    color="red lighten-2"
                    v-bind="attrs"
                    @click="removeOption(index, itemIndex)"
                    v-on="on"
                  >
                    delete
                  </v-icon>
                </template>
                <span>
                  Deleting or renaming an option that has dependencies will result in their deletion
                </span>
              </v-tooltip>
            </v-col>
          </v-row>
        </template>
        <BulkEditComponent
          v-if="bulkEditActive(index)"
          :initial-text="bulkEditText"
          @apply="applyBulkEditOptions(index, $event)"
          @cancel="hideBulkEdit"
        />
        <div class="d-flex justify-space-between">
          <vs-button
            class="pa-3"
            label="Delete Group"
            type="tertiary"
            destructive
            @click="removeGroup(index)"
          />
          <vs-button
            v-if="!bulkEditActive(index)"
            class="pa-3 mr-9 button-half-width"
            label="Add Option"
            type="secondary"
            @click="addOption(index)"
          />
          <vs-button
            v-if="!bulkEditActive(index)"
            class="pa-3 mr-9 button-half-width"
            label="Bulk Edit"
            type="secondary"
            @click="onBulkEditClicked(index)"
          />
        </div>
      </vs-card>
    </vs-wrapper>
    <vs-button label="Add Group" full-width type="secondary" @click="addGroup" />
  </vs-wrapper>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsCard from '@/components/vision/VsCard.vue'
import VsButton from '@/components/vision/VsButton.vue'
import BulkEditComponent from '@/components/form-builder/BulkEditComponent.vue'
import { OptionsGroup } from '@ironsight/form-builder'

interface Props {
  field: {
    optionGroups: OptionsGroup[]
    uniqueKey: () => string
  }
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (
    e: 'modify-field',
    payload: {
      propertyKey: string
      fieldValue: OptionsGroup[]
      key: string
    },
  ): void
}>()

const showBulkEdit = ref<boolean>(false)
const bulkEditText = ref<string>('')
const currentGroupIndex = ref<number | null>(null)

function addGroup() {
  emitChanges('optionGroups', [...props.field.optionGroups, new OptionsGroup('Group', [''])])
}

function removeGroup(index: number) {
  const groups = [...props.field.optionGroups]
  groups.splice(index, 1)
  emitChanges('optionGroups', groups)
}

function updateGroupName(title: string, index: number) {
  const groups = [...props.field.optionGroups]
  groups[index].title = title
  emitChanges('optionGroups', groups)
}

function addOption(index: number) {
  const groups = [...props.field.optionGroups]
  groups[index].items = [...groups[index].items, 'new option']
  emitChanges('optionGroups', groups)
}

function removeOption(index: number, itemIndex: number) {
  const groups = [...props.field.optionGroups]
  groups[index].items.splice(itemIndex, 1)
  emitChanges('optionGroups', groups)
}

function updateOptionText(text: string, index: number, itemIndex: number) {
  const groups = [...props.field.optionGroups]
  groups[index].items[itemIndex] = text
  emitChanges('optionGroups', groups)
}

function initializeBulkEditText(index: number) {
  bulkEditText.value = props.field.optionGroups[index].items.join('\n')
}

function bulkEditActive(index: number) {
  return showBulkEdit.value && currentGroupIndex.value === index
}

function onBulkEditClicked(index: number) {
  currentGroupIndex.value = index
  initializeBulkEditText(index)
  showBulkEdit.value = true
}

function hideBulkEdit() {
  showBulkEdit.value = false
  currentGroupIndex.value = null
}

function applyBulkEditOptions(index: number, bulkOptions: string[]) {
  const groups = [...props.field.optionGroups]
  groups[index].items = bulkOptions
  emitChanges('optionGroups', groups)
  hideBulkEdit()
}

function emitChanges(propertyKey: string, value: OptionsGroup[]) {
  emit('modify-field', {
    propertyKey,
    fieldValue: value,
    key: props.field.uniqueKey(),
  })
}
</script>

<style scoped>
.button-half-width {
  flex: 1;
}
</style>
